import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/lib/supabase'
import {
  signUp,
  signIn,
  signInWithGoogle as signInWithGoogleQuery,
  resetPassword,
  signOut as signOutQuery,
} from '@/lib/queries/auth'
import { REFERRAL_CODE_STORAGE_KEY, UTM_STORAGE_KEY } from '@/lib/constants'
import { setSentryUser, clearSentryUser } from '@/lib/sentry'
import { identifyUser, resetUser, capture } from '@/lib/posthog'
import { pixel } from '@/lib/pixel'

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content']

const SessionContext = createContext(null)

export const SUPER_ADMIN_EMAIL = 'mchap2023@gmail.com'

export function useSession() {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used within a SessionProvider')
  return ctx
}

function errorMessageFrom(err) {
  return err?.message || 'Something went wrong.'
}

export function SessionProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  // 1. On mount, restore session. Profile is loaded by ProfileContext's
  //    [user] effect — doing it here too would race its cancellation-guarded
  //    fetch.
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user)
        setSentryUser(session.user.id)
        identifyUser(session.user.id, session.user.email)
      }
      setLoading(false)
    })

    // 2. Listen for auth changes — only set user, NO async work here
    //    (Supabase v2 blocks signInWithPassword until this callback resolves,
    //    so any async Supabase call here creates a deadlock). ProfileContext
    //    clears its own profile state when `user` goes to null.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setUser(session.user)
        setSentryUser(session.user.id)
        identifyUser(session.user.id, session.user.email)
      } else {
        setUser(null)
        clearSentryUser()
        resetUser()
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Capture ?ref= from URL and store in localStorage for use at signup
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const ref = params.get('ref')
    if (ref) {
      localStorage.setItem(REFERRAL_CODE_STORAGE_KEY, ref)
    }
    const utm = {}
    for (const key of UTM_KEYS) {
      const value = params.get(key)
      if (value) utm[key] = value
    }
    if (Object.keys(utm).length > 0) {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm))
    }
  }, [])

  const signUpWithEmail = useCallback(async (email, password, displayName = '') => {
    // Profile row + referral record are created by the handle_new_user
    // trigger on auth.users (see 20260422000000_handle_new_user_trigger.sql).
    // The trigger reads display_name + referral_code from raw_user_meta_data.
    const referralCode = localStorage.getItem(REFERRAL_CODE_STORAGE_KEY) || ''
    const utmRaw = localStorage.getItem(UTM_STORAGE_KEY)
    const utm = utmRaw ? JSON.parse(utmRaw) : {}

    try {
      const data = await signUp(
        email,
        password,
        {
          display_name: displayName,
          referral_code: referralCode,
          ...utm,
        },
        `${window.location.origin}/onboarding`
      )

      const newUser = data.user

      // Supabase returns an empty identities array when the email is already registered
      if (newUser && newUser.identities && newUser.identities.length === 0) {
        return { error: 'An account already exists with this email. Please log in instead.' }
      }

      if (!newUser) {
        return { error: 'Something went wrong creating your account. Please try again.' }
      }

      if (referralCode) localStorage.removeItem(REFERRAL_CODE_STORAGE_KEY)
      if (utmRaw) localStorage.removeItem(UTM_STORAGE_KEY)
      pixel.trackRegistration()
      capture('signup_completed', utm)

      // Email confirmation enabled in Supabase Auth → data.session is null
      // until the user clicks the confirmation link. The auth-state useEffect
      // in Login.jsx won't fire (no session yet), so we tell the caller to
      // render a "Check your email" success state instead of leaving the form
      // sitting silently. data.user is populated regardless — the wrong
      // condition before (!newUser) never matched, which is why no feedback
      // appeared.
      if (!data.session) {
        return { error: null, needsConfirmation: true }
      }

      return { error: null }
    } catch (err) {
      return { error: errorMessageFrom(err) }
    }
  }, [])

  const signInWithEmail = useCallback(async (email, password) => {
    try {
      await signIn(email, password)
      return { error: null }
    } catch (err) {
      return { error: errorMessageFrom(err) }
    }
  }, [])

  const signInWithGoogle = useCallback(async () => {
    try {
      await signInWithGoogleQuery(window.location.href)
      return { error: null }
    } catch (err) {
      return { error: errorMessageFrom(err) }
    }
  }, [])

  const resetPasswordForEmail = useCallback(async email => {
    try {
      await resetPassword(email, window.location.origin + '/reset-password')
      return { error: null }
    } catch (err) {
      return { error: errorMessageFrom(err) }
    }
  }, [])

  const signOut = useCallback(async () => {
    try {
      await signOutQuery()
      setUser(null)
      return { error: null }
    } catch (err) {
      return { error: errorMessageFrom(err) }
    }
  }, [])

  const value = useMemo(
    () => ({
      user,
      loading,
      isSuperAdmin: user?.email === SUPER_ADMIN_EMAIL,
      signUpWithEmail,
      signInWithEmail,
      signInWithGoogle,
      resetPasswordForEmail,
      signOut,
    }),
    [
      user,
      loading,
      signUpWithEmail,
      signInWithEmail,
      signInWithGoogle,
      resetPasswordForEmail,
      signOut,
    ]
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

SessionProvider.displayName = 'SessionProvider'
